import React, { ReactElement, useEffect, useState } from 'react'
import { format } from 'date-fns'
import PageHeader from '../components/PageHeader'
import Table from '../components/Table'
import Radom, { Deposit, DepositAccount, DepositAccountType, DepositCryptoAddressType } from '../state/Radom'
import { observer } from 'mobx-react'
import { ButtonLeadingIcon, PrimaryButton } from '../components/Button'
import { formatCurrency, shortAddress, roundAssetAmount, successToast, useTestMode } from '../util/Util'
import { Container } from '../components/Animations'
import Pagination from '../components/Pagination'
import Dropdown, { DropdownItem } from '../components/Dropdown'
import Checkbox from '../components/Checkbox'
import Modal from '../components/Modal'
import { RADOM_COLORS } from '../util/Constants'
import CopyButton from '../components/CopyButton'
import CreateDepositAddressModal from '../components/CreateDepositAddressModal'
import { Link } from 'react-router-dom'
import { getMethod } from '../util/Managed'
import { Chevron } from '../icons/Chevron'
import TimeText from '../components/TimeText'
import LinkExternal from '../components/LinkExternal'
import { ExternalLinkText } from './Events'
import PaymentMethodDisplay from '../components/PaymentMethodDisplay'
import ConfirmButtonModal from '../components/ConfirmButtonModal'
import { formatDepositAccount, formatDepositAccountType } from '../components/Shared'
import { TextInput } from '../components/Input'
import AccountDetailsDrawer, { DrawerField } from '../components/AccountDetailsDrawer'
import LoadingBar from '../components/LoadingBar'
import User from '../state/User'
import {
  DashboardPage,
  InlinePill,
  PageActionRow,
  PageHeaderRow,
  SummaryLabel,
  SummaryMeta,
  SummaryStrip,
  SummaryStripItem,
  SummaryValue,
  Surface,
  SurfaceHeader,
  SurfaceTitle
} from '../components/DashboardPrimitives'
import Gate from '../components/Gate'
import { usePermissions } from '../util/permissions'

const DepositAddresses = observer((): ReactElement => {
  const pageSize = 10
  const [testMode] = useTestMode()
  const [isLoading, setIsLoading] = useState(false)
  const [curPage, setCurPage] = useState(0)
  const [exportDepositAddresses, setExportDepositAddresses] = useState(false)
  const [isCreateDepositAddressModalOpen, setIsCreateDepositAddressModalOpen] = useState(false)
  const [depositAddresses, setDepositAddresses] = useState<DepositAccount[]>([])
  const [totalDepositAddressesSize, setTotalDepositAddressesSize] = useState(0)
  const [accountTypes, setAccountTypes] = useState<DepositCryptoAddressType[]>([
    DepositCryptoAddressType.FirstParty, DepositCryptoAddressType.ThirdParty
  ])
  const [showDetailsPanel, setShowDetailsPanel] = useState(false)
  const [selectedAccount, setSelectedAccount] = useState<DepositAccount | null>(null)
  const [selectedTab, setSelectedTab] = useState<string>('Details')
  const [deposits, setDeposits] = useState<Deposit[]>([])
  const [isLoadingDeposits, setIsLoadingDeposits] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedPrivateTreasuryAddress, setSelectedPrivateTreasuryAddress] = useState<string | undefined>()

  const [metricsTotal, setMetricsTotal] = useState<number>(0)
  const [metricsLatestName, setMetricsLatestName] = useState<string | null>(null)
  const [metricsLatestDate, setMetricsLatestDate] = useState<string | null>(null)
  const [metricsVolume30d, setMetricsVolume30d] = useState<number>(0)
  const [isLoadingMetrics, setIsLoadingMetrics] = useState(false)
  const { has } = usePermissions()
  const defaultCurrency = User.organization?.defaultCurrency ?? 'USD'

  const fetchDepositAddresses = async (): Promise<void> => {
    setIsLoading(true)
    setDepositAddresses([])
    setTotalDepositAddressesSize(0)
    const isExternal = accountTypes.length === 2 || accountTypes.length === 0
      ? undefined
      : accountTypes[0] === DepositCryptoAddressType.ThirdParty
    const isSearching = searchQuery.trim().length > 0
    const offset = isSearching ? 0 : curPage * pageSize
    const limit = isSearching ? 250 : pageSize

    Radom.listDepositAccounts(testMode, offset, limit, isExternal, [DepositAccountType.Crypto])
      .then(res => {
        setDepositAddresses(res.data)
        setTotalDepositAddressesSize(res.totalCount)
      })
      .finally(() => setIsLoading(false))
  }

  const fetchMetrics = async (): Promise<void> => {
    setIsLoadingMetrics(true)
    try {
      const metrics = await Radom.getDepositAccountMetrics(testMode)
      setMetricsTotal(metrics.totalCount)
      setMetricsLatestName(metrics.latestName)
      setMetricsLatestDate(metrics.latestCreatedAt)
      setMetricsVolume30d(metrics.volume30d)
    } catch (err) {
      console.error('Failed to load deposit metrics', err)
    } finally {
      setIsLoadingMetrics(false)
    }
  }

  const fetchDeposits = async (id: string): Promise<void> => {
    if (!has('ReadDeposit')) return
    setIsLoadingDeposits(true)
    setDeposits([])

    Radom.listDepositsByAccountId(id)
      .then(res => {
        setDeposits(res)
      })
      .finally(() => setIsLoadingDeposits(false))
  }

  useEffect(() => {
    fetchDepositAddresses()
  }, [testMode, curPage, accountTypes, searchQuery])

  useEffect(() => {
    fetchMetrics()
  }, [testMode])

  useEffect(() => {
    setCurPage(0)
  }, [searchQuery, accountTypes, testMode])

  useEffect(() => {
    const treasuryId = selectedAccount?.accountDetails.Crypto?.treasuryId
    setSelectedPrivateTreasuryAddress(undefined)
    if (!treasuryId || !User.hasPermission('ReadPrivateTreasury')) {
      return
    }

    let isMounted = true
    Radom.getPrivateTreasury()
      .then((treasury) => {
        if (isMounted && treasury?.id === treasuryId) {
          setSelectedPrivateTreasuryAddress(treasury.evmAddress)
        }
      })
      .catch(() => undefined)

    return () => {
      isMounted = false
    }
  }, [selectedAccount?.accountDetails.Crypto?.treasuryId])

  const normalizedSearchQuery = searchQuery.trim().toLowerCase()
  const filteredDepositAddresses = depositAddresses.filter(account => {
    if (!normalizedSearchQuery) {
      return true
    }

    const terms = [
      account.id,
      account.name,
      account.accountDetails.Crypto?.address,
      account.customer?.name,
      account.customer?.email,
      account.customer?.id
    ]

    return terms.some(term => term?.toLowerCase().includes(normalizedSearchQuery))
  })
  const visibleDepositAddresses = normalizedSearchQuery
    ? filteredDepositAddresses.slice(curPage * pageSize, (curPage + 1) * pageSize)
    : filteredDepositAddresses
  const visibleDepositAddressCount = normalizedSearchQuery ? filteredDepositAddresses.length : totalDepositAddressesSize

  return <Container>
    <DashboardPage>
      <PageHeaderRow>
        <PageHeader title='Deposit addresses' />
        <Gate permission='WriteDepositAccount'>
          <PageActionRow>
            <PrimaryButton onClick={() => setIsCreateDepositAddressModalOpen(true)}>
              <ButtonLeadingIcon>+</ButtonLeadingIcon>
              <span>Create address</span>
            </PrimaryButton>
          </PageActionRow>
        </Gate>
      </PageHeaderRow>

      <Surface>
        <SurfaceHeader>
          <div>
            <SurfaceTitle>Overview</SurfaceTitle>
          </div>
          <InlinePill $tone={testMode ? 'amber' : 'green'}>
            {testMode ? 'Test mode' : 'Live mode'}
          </InlinePill>
        </SurfaceHeader>
        <SummaryStrip>
          <SummaryStripItem>
            <SummaryLabel>Total accounts</SummaryLabel>
            <SummaryValue>
              {isLoadingMetrics ? <LoadingBar style={{ height: 28, width: 64 }} /> : metricsTotal}
            </SummaryValue>
            <SummaryMeta>Deposit addresses</SummaryMeta>
          </SummaryStripItem>
          <SummaryStripItem>
            <SummaryLabel>30-day volume</SummaryLabel>
            <SummaryValue>
              {isLoadingMetrics
                ? <LoadingBar style={{ height: 28, width: 130 }} />
                : formatCurrency(metricsVolume30d, defaultCurrency)}
            </SummaryValue>
            <SummaryMeta>Estimated in {defaultCurrency}</SummaryMeta>
          </SummaryStripItem>
          <SummaryStripItem>
            <SummaryLabel>Latest added</SummaryLabel>
            <SummaryValue>
              {isLoadingMetrics
                ? <LoadingBar style={{ height: 28, width: 76 }} />
                : metricsLatestDate
                  ? format(new Date(metricsLatestDate), 'MMM d')
                  : 'None'}
            </SummaryValue>
            <SummaryMeta>
              {metricsLatestName ?? 'No addresses created yet'}
            </SummaryMeta>
          </SummaryStripItem>
        </SummaryStrip>
      </Surface>

      <Surface>
        <SurfaceHeader>
          <div>
            <SurfaceTitle>Deposit addresses</SurfaceTitle>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ minWidth: 220 }}>
              <TextInput
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder='Search by address, name, contact, or ID'
              />
            </div>
            <Dropdown
              selectedContent={<div style={{ fontSize: 14 }}>Account type</div>}
              dropdownContent={<div>
                {Object.values(DepositCryptoAddressType).map(s =>
                  <DropdownItem
                    key={s}
                    style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: 14 }}
                    onClick={() => {
                      setAccountTypes((prev) =>
                        prev.includes(s)
                          ? prev.filter((x) => x !== s)
                          : [...prev, s])
                    }}>
                    <Checkbox size={16} checked={accountTypes.includes(s)} />
                    <span>{s}</span>
                  </DropdownItem>
                )}
              </div>} />
          </div>
        </SurfaceHeader>
        <Table
          onClickRow={(rowIndex: number) => {
            const d = visibleDepositAddresses[rowIndex]
            setSelectedTab('Details')
            setSelectedAccount(d)
            setShowDetailsPanel(true)
            if (deposits.length === 0 ||
                deposits[0].depositAccountId !==
                d.id) { fetchDeposits(d.id) }
          }}
          borderless
          headers={['Address name', 'Related entity', 'Deposit address', 'Supported assets'].map(h => <span key={h}>{h}</span>)}
          items={
            visibleDepositAddresses.map((d) => [
              <div key={0} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ color: RADOM_COLORS.BLACK, fontWeight: 500 }}>{d.name}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ opacity: 0.58, fontSize: 13, fontFamily: 'var(--font-mono)' }}>{shortAddress(d.id, 6)}</span>
                  <CopyButton text={d.id} />
                </div>
              </div>,
              <div key={2}>
                {d?.accountDetails.Crypto?.isExternal && d.customer
                  ? <Link to={`/customer/${d.customer.id}`} style={{ color: RADOM_COLORS.BLACK }}>
                    {d.customer.name.length
                      ? d.customer.name
                      : d.customer.email}
                  </Link>
                  : 'Own address'}
              </div>,
              <div key={3} style={{ display: 'flex', gap: 4 }}>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13.5, color: RADOM_COLORS.BLACK }}>
                  {formatDepositAccount(d.accountDetails)}
                </span>
                {d.accountDetails.Crypto && <CopyButton text={d.accountDetails.Crypto?.address ?? ''} />}
              </div>,
              <div key={4}>
                {formatDepositAccountType(d.accountDetails)}
              </div>
            ])
          }
          isLoading={isLoading}
          emptyPlaceholderText={normalizedSearchQuery ? 'No deposit addresses match this search' : 'No deposit addresses yet'}
        />
        {!isLoading && <Pagination
          curPage={curPage}
          numPages={Math.ceil(visibleDepositAddressCount / pageSize)}
          onPageClick={page => setCurPage(page)}
        />}
      </Surface>

      <AccountDetailsDrawer
        visible={showDetailsPanel}
        onClose={() => setShowDetailsPanel(false)}
        eyebrow='Deposit address'
        title={selectedAccount?.name}
        tabs={['Details'].concat(has('ReadDeposit') ? ['Deposits'] : [])}
        selectedTab={selectedTab}
        onTabChange={setSelectedTab}
        menu={
          has('WriteDepositAccount') && <ConfirmButtonModal
            title='Archive address'
            description='Are you sure you want to archive this deposit address?'
            action='Archive address'
            onConfirm={async () => {
              if (!selectedAccount) return
              setShowDetailsPanel(false)
              Radom.deleteDepositAccount(selectedAccount.id)
                .then(async () => await fetchDepositAddresses())
                .then(() => {
                  successToast('Deposit address archived')
                })
            }}
            ActionComponent={(props: any) => <DropdownItem {...props}>Archive address</DropdownItem>}
          />
        }
      >
        {selectedTab === 'Details'
          ? <>
            {selectedAccount?.customer && <DrawerField label='Related Beneficiary'>
              <Link to={`/customer/${selectedAccount.customer.id}`} style={{ color: RADOM_COLORS.BLACK }}>
                {selectedAccount.customer.name.length
                  ? selectedAccount.customer.name
                  : selectedAccount.customer.email}
              </Link>
            </DrawerField>}

            <DrawerField
              label='Deposit Address ID'
              mono
              value={selectedAccount?.id}
              copy={selectedAccount?.id ?? ''}
            />

            <DrawerField
              label='Deposit Address'
              mono
              value={selectedAccount?.accountDetails.Crypto?.address}
              copy={selectedAccount?.accountDetails.Crypto?.address ?? ''}
            />

            <DrawerField
              label='Treasury'
              value={selectedAccount?.accountDetails.Crypto?.treasuryId ? 'Private treasury' : 'Radom treasury'}
              trailing={selectedAccount?.accountDetails.Crypto?.treasuryId && selectedPrivateTreasuryAddress
                ? <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ color: RADOM_COLORS.GRAY_DARKER, fontFamily: 'var(--font-mono)', fontSize: 13 }}>
                    {shortAddress(selectedPrivateTreasuryAddress, 6)}
                  </span>
                  <CopyButton text={selectedPrivateTreasuryAddress} />
                </div>
                : undefined}
            />

            <DrawerField label='Supported Assets' boxed={false}>
              {selectedAccount?.accountDetails.Crypto?.depositMethods.map(m =>
                <PaymentMethodDisplay
                  key={m.network + (m.token ?? '')}
                  method={{ network: m.network, token: m.token }}
                  excludeName
                  showNetworkBadge
                  disableTooltip={false} />
              )}
            </DrawerField>
          </>
          : <>
            <Table
              borderless
              disableOnClickState
              headers={['Deposit ID', 'Summary', 'Date'].map(h => <span key={h}>{h}</span>)}
              isLoading={isLoadingDeposits}
              items={deposits.map(d => {
                let summary: {
                  in: {
                    img: string
                    amount: number | string
                    ticker: string
                  }
                  out?: {
                    img: string
                    amount: number | string
                    ticker: string
                  }
                }

                if (d.depositMethod.Crypto) {
                  const method = getMethod(d.depositMethod.Crypto.network, d.depositMethod.Crypto.token)
                  summary = {
                    in: {
                      img: method.logo,
                      amount: d.netAmount,
                      ticker: method.ticker
                    }
                  }
                } else {
                  const outMethod = getMethod(d.depositMethod.Fiat?.onrampNetwork || '', d.depositMethod.Fiat?.onrampToken)
                  const inMethod = getMethod('Fiat', d.depositMethod.Fiat?.fiatCurrency)

                  summary = {
                    in: {
                      img: inMethod.logo,
                      amount: d.fiatGrossAmount || '',
                      ticker: inMethod.ticker
                    },
                    out: {
                      img: outMethod.logo,
                      amount: d.netAmount,
                      ticker: outMethod.ticker
                    }
                  }
                }
                return [
                  <div key={'1'}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <ExternalLinkText
                        onClick={e => e.stopPropagation()}
                        target="_blank"
                        href={`/deposit/${d.id}?source=transactions`}>
                        {shortAddress(d.id, 3)}
                        <LinkExternal stroke={RADOM_COLORS.BLACK} width={14} />
                      </ExternalLinkText>
                      <CopyButton text={d.id} />
                    </div>
                  </div>,
                  <div key={'2'} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <img src={summary.in.img} style={{ width: 20, height: 20 }} />
                    <span>
                      {roundAssetAmount(summary.in.ticker, Number(summary.in.amount))}
                    </span>
                    <span>{summary.in.ticker}</span>
                    {
                      summary.out && <>
                        <Chevron style={{ opacity: 0.65, transform: 'rotate(-90deg)' }} />
                        <img src={summary.out.img} style={{ width: 20, height: 20 }} />
                        <span>
                          {roundAssetAmount(summary.out.ticker, Number(summary.out.amount))}
                        </span>
                        <span>{summary.out.ticker}</span>
                      </>
                    }
                  </div>,
                  <div key={'3'}>
                    <TimeText date={new Date(d.depositedAt)} />
                  </div>
                ]
              })}
              emptyPlaceholderText='No deposits yet'
            />
          </>}
      </AccountDetailsDrawer>

      {
        exportDepositAddresses &&
        <Modal visible={exportDepositAddresses} onClose={() => setExportDepositAddresses(false)} title='Export deposit addresses'>
          <></>
        </Modal>
      }

      {
        isCreateDepositAddressModalOpen &&
          <CreateDepositAddressModal
            title='Create deposit address'
            visible={isCreateDepositAddressModalOpen}
            onClose={() => setIsCreateDepositAddressModalOpen(false)}
            onAccountCreated={() => {
              setIsCreateDepositAddressModalOpen(false)
              fetchDepositAddresses()
              fetchMetrics()
            }} />
      }
    </DashboardPage>
  </Container>
})

export default DepositAddresses
